import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { Box, getCSSVariable, Text } from '@opendoor/bricks-next';
import { EntryComponent } from 'cms/entries/entries';
import { ILpComponentComparisonTable } from 'declarations/contentful';

import styles from './ComparisonTable.module.css';
import Container from './shared/Container';
import { TitleWithAccent } from './shared/Typography';

const Yes = () => (
  <Text
    tag="p"
    typography="$bodyLarge"
    display="flex"
    justifyContent="center"
    alignItems="center"
    color="unset"
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 8.03846L6 11.5L14 4" stroke="currentColor" strokeWidth="2" />
    </svg>
  </Text>
);

const No = () => (
  <Text
    tag="p"
    typography="$bodyLarge"
    display="flex"
    justifyContent="center"
    alignItems="center"
    color="unset"
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 13.0007L13 3" stroke="currentColor" strokeWidth="2" />
      <path d="M13 13L8.00005 7.99984L3 3" stroke="currentColor" strokeWidth="2" />
    </svg>
  </Text>
);

const Question = () => (
  <Text
    tag="p"
    typography="$subheaderMedium"
    display="flex"
    justifyContent="center"
    alignItems="center"
    color="unset"
  >
    ?
  </Text>
);

const Dash = () => (
  <Text
    tag="p"
    typography="$subheaderMedium"
    display="flex"
    justifyContent="center"
    alignItems="center"
    color="unset"
  >
    -
  </Text>
);

const options: Options = {
  renderNode: {
    [BLOCKS.TABLE]: (_, children) => children,
    [BLOCKS.TABLE_ROW]: (_, children) => <tr>{children}</tr>,
    [BLOCKS.TABLE_HEADER_CELL]: (_, children) => <th>{children}</th>,
    [BLOCKS.TABLE_CELL]: (_: any, children: any) => {
      if (children[0].type == 'u') {
        return <th scope="row">{children[0].props.children}</th>;
      }
      return <td>{children}</td>;
    },
    [BLOCKS.PARAGRAPH]: (_: any, children: any) => {
      if (children[0] == '✓') return <Yes />;
      if (children[0] == 'X') return <No />;
      if (children[0] == '?') return <Question />;
      if (children[0] == '-') return <Dash />;
      return children[0];
    },
  },
};

const RenderComparisonTable = (entry: ILpComponentComparisonTable) => {
  const { fields } = entry;
  const tableId = `comparison-table-${entry.sys.id}`;
  // Contentful field is typed as 'Brand Blue 600' | 'Warm Grey 950' — map to semantic tokens
  const highlightColor =
    fields.highlightColor === 'Warm Grey 950'
      ? getCSSVariable('$backgroundInversePrimary')
      : getCSSVariable('$contentAccentBrand');
  const borderPrimary = getCSSVariable('$borderPrimary');
  const hasBorder =
    fields.layout === 'Border' || fields.layout === 'Earth 200' || fields.layout === 'Grey';
  const isNoBorder = fields.layout === 'No Border';
  const hasBGAndBorder =
    fields.image && (fields.layout === 'Earth 200' || fields.layout === 'Grey');
  const alignClass = fields.tableTextAlign === 'Center' ? styles.centerAlign : styles.leftAlign;

  const tableClasses = [
    styles.table,
    alignClass,
    hasBorder && styles.borderLayout,
    isNoBorder && styles.noBorderLayout,
  ]
    .filter(Boolean)
    .join(' ');

  // CSS variables for layout variants
  const cssVars = {
    '--border-primary': borderPrimary,
    '--accent-bg': getCSSVariable('$backgroundAccentBrand'),
  } as React.CSSProperties;

  // Dynamic nth-child selectors for highlight/opendoor columns (can't use CSS variables in nth-child)
  let columnCss = '';
  if (fields.highlightColumn) {
    const col = fields.highlightColumn;
    const inverseText = getCSSVariable('$contentInversePrimary');
    columnCss += `
      #${tableId} th:not([scope=row]):nth-child(${col}) { background-color: ${highlightColor}; color: ${inverseText}; }
      #${tableId} td:nth-child(${col}) { border-left: 1px solid ${highlightColor}; border-right: 1px solid ${highlightColor}; }
      #${tableId} tbody tr:last-child td:nth-child(${col}) { border-bottom: 1px solid ${highlightColor}; }
    `;
  }
  if (fields.opendoorColumn) {
    columnCss += `
      #${tableId} tbody td:nth-child(${fields.opendoorColumn}) { color: ${getCSSVariable(
      '$contentAccentBrand',
    )}; }
    `;
  }

  return (
    <Container py="$6">
      {fields.title && (
        <TitleWithAccent
          title={fields.title}
          titleAccent={fields.titleAccent}
          titleSize={fields.titleSize}
          mb="$16"
          mx={fields.titleAlign === 'Center' ? 'auto' : 0}
          textAlign={fields.titleAlign === 'Center' ? 'center' : 'left'}
        />
      )}
      <Box
        display="block"
        gap="$5"
        backgroundColor={hasBGAndBorder ? '$backgroundTertiary' : undefined}
        borderRadius={hasBGAndBorder ? '$8x' : undefined}
        padding={hasBGAndBorder ? '$5' : 0}
        $largerThanMD={{ display: fields.image ? 'flex' : 'block', flexDirection: 'row' }}
      >
        <Box flex={fields.image ? 6 : undefined} flexBasis={fields.image ? 0 : undefined}>
          <Box
            borderRadius="$8x"
            borderColor={fields.layout === 'Border' ? '$borderPrimary' : undefined}
            borderWidth={fields.layout === 'Border' ? '$0dot5x' : undefined}
            $platform-web={{ overflow: 'auto' }}
          >
            {columnCss && <style dangerouslySetInnerHTML={{ __html: columnCss }} />}
            <table id={tableId} className={tableClasses} style={cssVars}>
              {fields.tableHeader && (
                <thead>{documentToReactComponents(fields.tableHeader, options)}</thead>
              )}
              {fields.tableBody && (
                <tbody>{documentToReactComponents(fields.tableBody, options)}</tbody>
              )}
            </table>
          </Box>
        </Box>
        {fields.image && (
          <Box flex={4} flexBasis={fields.image ? 0 : undefined}>
            <img
              className={styles.tableImage}
              src={fields.image.fields?.file?.url}
              alt={fields.image.fields?.description || fields.image.fields?.title}
            />
          </Box>
        )}
      </Box>
    </Container>
  );
};

const ComparisonTable: EntryComponent<ILpComponentComparisonTable> = {
  render: RenderComparisonTable,
};

export default ComparisonTable;
